import { Component, useEffect, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Database, Download, Eye, Filter, Menu, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

type NavLayout = 'horizontal' | 'hamburger';
type SortOrder = 'asc' | 'desc';

interface ColumnInfo {
  name: string;
  dataType: string;
  ordinal: number;
  isSensitive: boolean;
}

interface TableInfo {
  name: string;
  label: string;
  category: string;
  description: string;
  rowCountEstimate: number | null;
  timeColumn: string | null;
  columns: ColumnInfo[];
}

interface TablesResponse { tables: TableInfo[]; }
interface RowsResponse {
  table: TableInfo;
  rows: Record<string, unknown>[];
  total: number;
  offset: number;
  limit: number;
  orderBy?: string | null;
  order?: SortOrder;
}

type AuditGovernanceSectionId = 'permissionChanges' | 'roleChanges' | 'customerAccessChanges' | 'monthlySnapshotSaves';

interface AuditGovernanceRow {
  ts: string;
  user_email?: string | null;
  user_role?: string | null;
  action?: string | null;
  method?: string | null;
  path?: string | null;
  status?: number | null;
  payload_summary?: unknown;
}

interface AuditGovernanceResponse {
  generatedAt?: string;
  permissionChanges: AuditGovernanceRow[];
  roleChanges: AuditGovernanceRow[];
  customerAccessChanges: AuditGovernanceRow[];
  monthlySnapshotSaves: AuditGovernanceRow[];
}

interface LogMasterFilters {
  search: string;
  userEmail: string;
  customerId: string;
  incidentId: string;
  action: string;
  fromTs: string;
  toTs: string;
}

const PAGE_SIZE = 100;
const FILTERS_EMPTY: LogMasterFilters = {
  search: '',
  userEmail: '',
  customerId: '',
  incidentId: '',
  action: '',
  fromTs: '',
  toTs: '',
};

function qsFor(tableName: string, filters: LogMasterFilters, offset: number, limit = PAGE_SIZE, orderBy?: string | null, order: SortOrder = 'desc'): string {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  if (orderBy) qs.set('orderBy', orderBy);
  qs.set('order', order);
  if (filters.search.trim()) qs.set('search', filters.search.trim());
  if (filters.userEmail.trim()) qs.set('userEmail', filters.userEmail.trim());
  if (filters.customerId.trim()) qs.set('customerId', filters.customerId.trim());
  if (filters.incidentId.trim()) qs.set('incidentId', filters.incidentId.trim());
  if (filters.action.trim()) qs.set('action', filters.action.trim());
  if (filters.fromTs) qs.set('fromTs', new Date(filters.fromTs).toISOString());
  if (filters.toTs) qs.set('toTs', new Date(filters.toTs).toISOString());
  return `/log-master/tables/${encodeURIComponent(tableName)}/rows?${qs.toString()}`;
}

function csvHref(tableName: string, filters: LogMasterFilters, orderBy?: string | null, order: SortOrder = 'desc'): string {
  const qs = new URLSearchParams();
  qs.set('limit', '10000');
  if (orderBy) qs.set('orderBy', orderBy);
  qs.set('order', order);
  if (filters.search.trim()) qs.set('search', filters.search.trim());
  if (filters.userEmail.trim()) qs.set('userEmail', filters.userEmail.trim());
  if (filters.customerId.trim()) qs.set('customerId', filters.customerId.trim());
  if (filters.incidentId.trim()) qs.set('incidentId', filters.incidentId.trim());
  if (filters.action.trim()) qs.set('action', filters.action.trim());
  if (filters.fromTs) qs.set('fromTs', new Date(filters.fromTs).toISOString());
  if (filters.toTs) qs.set('toTs', new Date(filters.toTs).toISOString());
  return `${getApiBase()}/log-master/tables/${encodeURIComponent(tableName)}.csv?${qs.toString()}`;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    try {
      return String(value);
    } catch {
      return '[unrenderable]';
    }
  }
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function tableColumns(table?: TableInfo | null): ColumnInfo[] {
  return Array.isArray(table?.columns) ? table!.columns : [];
}

function preferredColumns(table?: TableInfo | null): ColumnInfo[] {
  if (!table) return [];
  const cols = tableColumns(table);
  const preferred = [
    table.timeColumn,
    'ts',
    'created_at',
    'created_at_utc',
    'updated_at',
    'user_email',
    'updated_by_email',
    'customer_id',
    'incident_id',
    'action',
    'status',
    'method',
    'path',
    'name',
    'title',
    'severity',
    'state',
    'category',
  ].filter(Boolean) as string[];
  const seen = new Set<string>();
  const ordered: ColumnInfo[] = [];
  for (const name of preferred) {
    const col = cols.find((c) => c.name === name);
    if (col && !seen.has(col.name)) {
      ordered.push(col);
      seen.add(col.name);
    }
  }
  for (const col of cols) {
    if (!seen.has(col.name)) {
      ordered.push(col);
      seen.add(col.name);
    }
    if (ordered.length >= 14) break;
  }
  return ordered;
}

function categoryBadgeClass(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('audit')) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900';
  if (c.includes('response')) return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-900';
  if (c.includes('hunt')) return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900';
  if (c.includes('master')) return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900';
  return 'bg-muted text-muted-foreground border-border';
}

const AUDIT_GOVERNANCE_SECTIONS: Array<{ id: AuditGovernanceSectionId; title: string; helper: string }> = [
  { id: 'permissionChanges', title: 'Permission Changes', helper: 'Feature flags, capability matrix, KQL table access and report-access policy updates.' },
  { id: 'roleChanges', title: 'Role Changes', helper: 'SOC user creation, role assignment and role-sensitive user administration events.' },
  { id: 'customerAccessChanges', title: 'User / Customer Access Changes', helper: 'Customer assignment, customer access and permission-scope changes.' },
  { id: 'monthlySnapshotSaves', title: 'Monthly Snapshot Save / Re-save Activity', helper: 'Manual or automated monthly report snapshot persistence activity.' },
];

const AUDIT_GOVERNANCE_NAV_KEY = '__audit_governance__';

function formatAuditTime(value: unknown): string {
  if (value === null || value === undefined) return '-';
  const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : displayValue(value);
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
}

function auditCsvHref(sectionId: AuditGovernanceSectionId): string {
  return `${getApiBase()}/log-master/audit-governance/${encodeURIComponent(sectionId)}.csv?limit=5000`;
}

function AuditGovernanceTable({ title, helper, sectionId, rows, exportAllowed }: { title: string; helper: string; sectionId: AuditGovernanceSectionId; rows: AuditGovernanceRow[]; exportAllowed: boolean }) {
  const safeRows = asArray<AuditGovernanceRow>(rows);
  return (
    <section className="rounded-xl border border-border bg-white dark:bg-card shadow p-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{helper}</p>
        </div>
        {exportAllowed && (
          <button type="button" onClick={() => window.open(auditCsvHref(sectionId), '_blank')} className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 text-primary px-3 py-2 text-xs font-semibold hover:bg-primary/20">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        )}
      </div>

      {safeRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matching audit entries found.</p>
      ) : (
        <div className="overflow-auto border border-border rounded-xl">
          <table className="min-w-full text-xs">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Path</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {safeRows.map((row, idx) => (
                <tr key={`${row.ts}-${row.action ?? 'audit'}-${idx}`} className="border-t border-border hover:bg-muted/40">
                  <td className="px-3 py-2 whitespace-nowrap">{formatAuditTime(row.ts)}</td>
                  <td className="px-3 py-2">{displayValue(row.user_email).replace('-', '-')}</td>
                  <td className="px-3 py-2">{displayValue(row.user_role).replace('-', '-')}</td>
                  <td className="px-3 py-2 font-mono">{displayValue(row.action).replace('-', '-')}</td>
                  <td className="px-3 py-2 font-mono max-w-[520px] truncate" title={displayValue(row.path)}>{displayValue(row.path).replace('-', '-')}</td>
                  <td className="px-3 py-2">{row.status ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

class LogMasterSectionErrorBoundary extends Component<{ children: ReactNode; title: string }, { message: string | null }> {
  state: { message: string | null } = { message: null };

  static getDerivedStateFromError(error: unknown): { message: string } {
    return { message: error instanceof Error ? error.message : 'Unexpected rendering error' };
  }

  componentDidCatch(error: unknown): void {
    console.error('[LogMaster] section render failed', error);
  }

  render() {
    if (this.state.message) {
      return (
        <section className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
          <div className="font-bold">{this.props.title} could not be rendered.</div>
          <div className="mt-1">{this.state.message}</div>
          <div className="mt-2 text-xs opacity-80">The rest of Log Master remains available. Please capture the browser console message if this appears again.</div>
        </section>
      );
    }
    return this.props.children;
  }
}

function AuditGovernancePanel({ exportAllowed }: { exportAllowed: boolean }) {
  const auditQuery = useQuery({
    queryKey: ['log-master', 'audit-governance'],
    queryFn: async () => apiFetch<AuditGovernanceResponse>('/log-master/audit-governance?limit=25'),
  });

  const data = auditQuery.data;
  const errorMessage = auditQuery.error instanceof Error ? auditQuery.error.message : auditQuery.error ? 'Failed to load Audit Governance' : null;

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-border bg-white dark:bg-card shadow p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">Audit Governance</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Privileged governance audit views have been moved here from Masters. Use these panels for quick review, or use the PostgreSQL table browser below for deeper filtering.
            </p>
            {data?.generatedAt && <p className="text-[11px] text-muted-foreground mt-1">Last loaded: {formatAuditTime(data.generatedAt)}</p>}
          </div>

          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted" onClick={() => void auditQuery.refetch()} disabled={auditQuery.isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 ${auditQuery.isFetching ? 'animate-spin' : ''}`} />
            {auditQuery.isFetching ? 'Refreshing...' : 'Refresh Audit'}
          </button>
        </div>

        {errorMessage && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 text-red-700 dark:text-red-300 p-3 text-sm">
            {errorMessage}
          </div>
        )}
      </section>

      {auditQuery.isLoading && (
        <div className="rounded-xl border border-border bg-muted/30 p-5 text-sm text-muted-foreground">Loading privileged audit governance activity…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-4">
          {AUDIT_GOVERNANCE_SECTIONS.map((section) => (
            <AuditGovernanceTable
              key={section.id}
              title={section.title}
              helper={section.helper}
              sectionId={section.id}
              rows={asArray<AuditGovernanceRow>(((data as unknown) as Record<string, unknown> | undefined)?.[section.id])}
              exportAllowed={exportAllowed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function LogMasterTab() {
  const { canCapability } = useAuth();
  const [navLayout, setNavLayout] = useState<NavLayout>(() => {
    if (typeof window === 'undefined') return 'hamburger';
    return window.localStorage.getItem('log-master-nav-layout') === 'horizontal' ? 'horizontal' : 'hamburger';
  });
  const [navOpen, setNavOpen] = useState(false);
  const [activeNavKey, setActiveNavKey] = useState<string>(AUDIT_GOVERNANCE_NAV_KEY);
  const [draftFilters, setDraftFilters] = useState<LogMasterFilters>(FILTERS_EMPTY);
  const [appliedFilters, setAppliedFilters] = useState<LogMasterFilters>(FILTERS_EMPTY);
  const [offset, setOffset] = useState(0);
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('log-master-nav-layout', navLayout);
    if (navLayout === 'horizontal') setNavOpen(false);
  }, [navLayout]);

  const tablesQuery = useQuery({
    queryKey: ['log-master', 'tables'],
    queryFn: async () => apiFetch<TablesResponse>('/log-master/tables'),
  });

  const tables = Array.isArray(tablesQuery.data?.tables) ? tablesQuery.data.tables : [];
  const isAuditGovernanceActive = activeNavKey === AUDIT_GOVERNANCE_NAV_KEY;
  const activeTable = useMemo(() => {
    if (activeNavKey === AUDIT_GOVERNANCE_NAV_KEY) return null;
    return tables.find((t) => t.name === activeNavKey) ?? tables[0] ?? null;
  }, [tables, activeNavKey]);
  const activeNavLabel = isAuditGovernanceActive ? 'Audit Governance' : activeTable?.label ?? 'Select view';
  const activeColumns = useMemo(() => preferredColumns(activeTable), [activeTable]);
  const allActiveColumns = useMemo(() => tableColumns(activeTable), [activeTable]);
  const exportAllowed = canCapability('log_master', 'export');

  useEffect(() => {
    if (!isAuditGovernanceActive && tables.length > 0 && !tables.some((table) => table.name === activeNavKey)) {
      setActiveNavKey(tables[0].name);
    }
  }, [activeNavKey, isAuditGovernanceActive, tables]);

  useEffect(() => {
    if (activeTable && !orderBy) {
      const cols = tableColumns(activeTable);
      setOrderBy(activeTable.timeColumn ?? cols[0]?.name ?? null);
    }
  }, [activeTable?.name, activeTable?.timeColumn, orderBy]);

  const rowsQuery = useQuery({
    enabled: Boolean(activeTable?.name),
    queryKey: ['log-master', 'rows', activeTable?.name, appliedFilters, offset, orderBy, order],
    queryFn: async () => {
      if (!activeTable?.name) throw new Error('No table selected');
      return apiFetch<RowsResponse>(qsFor(activeTable.name, appliedFilters, offset, PAGE_SIZE, orderBy, order));
    },
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(rowsQuery.data?.rows) ? rowsQuery.data.rows : [];
  const total = typeof rowsQuery.data?.total === 'number' ? rowsQuery.data.total : 0;
  const loading = rowsQuery.isFetching || tablesQuery.isFetching;
  const error = rowsQuery.error || tablesQuery.error;

  function selectAuditGovernance(): void {
    setActiveNavKey(AUDIT_GOVERNANCE_NAV_KEY);
    setNavOpen(false);
  }

  function selectTable(tableName: string): void {
    setActiveNavKey(tableName);
    setOffset(0);
    setOrderBy(null);
    setNavOpen(false);
  }

  function applyFilters(): void {
    setAppliedFilters(draftFilters);
    setOffset(0);
  }

  function resetFilters(): void {
    setDraftFilters(FILTERS_EMPTY);
    setAppliedFilters(FILTERS_EMPTY);
    setOffset(0);
  }

  function toggleSort(column: string): void {
    setOffset(0);
    if (orderBy === column) {
      setOrder((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('desc');
    }
  }

  const grouped = useMemo(() => {
    const map = new Map<string, TableInfo[]>();
    for (const table of tables) {
      const list = map.get(table.category) ?? [];
      list.push(table);
      map.set(table.category, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [tables]);

  const errorMessage = error instanceof Error ? error.message : error ? 'Failed to load Log Master data' : null;

  return (
    <div className="slide-in space-y-4">
      <div className="bg-white dark:bg-card rounded-xl shadow p-5 border border-border/40">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-bold text-foreground">Log Master</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-4xl">
              Central PostgreSQL-backed audit/log explorer for privileged actions, case evidence audit timelines, hunting history, ResponseOps activity, source checks and operational DB history.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900">
                <ShieldCheck className="h-3.5 w-3.5" /> Sensitive fields redacted server-side
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900">
                <Eye className="h-3.5 w-3.5" /> Reads and exports are audited
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 text-[11px] font-semibold text-foreground">
              <span className="text-muted-foreground">Layout</span>
              <select className="h-7 rounded-md border border-border bg-background px-2 text-xs font-semibold" value={navLayout} onChange={(e) => setNavLayout(e.target.value as NavLayout)}>
                <option value="hamburger">Hamburger menu</option>
                <option value="horizontal">Horizontal tabs</option>
              </select>
            </label>
            <button type="button" onClick={() => { void tablesQuery.refetch(); void rowsQuery.refetch(); }} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-semibold hover:bg-muted">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            {activeTable && exportAllowed && (
              <button type="button" onClick={() => window.open(csvHref(activeTable.name, appliedFilters, orderBy, order), '_blank')} className="inline-flex h-9 items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 text-primary px-3 text-xs font-semibold hover:bg-primary/20">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="relative rounded-xl border border-border bg-white dark:bg-card shadow p-3">
        {navLayout === 'hamburger' ? (
          <>
            <button type="button" onClick={() => setNavOpen((open) => !open)} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-sm font-bold hover:bg-muted" aria-haspopup="menu" aria-expanded={navOpen}>
              <Menu className="h-4 w-4" /> Log Master Navigation
              <span className="text-xs font-normal text-muted-foreground">{activeNavLabel}</span>
            </button>
            {navOpen && (
              <div className="absolute left-3 top-14 z-40 w-[min(94vw,520px)] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-muted/40">
                  <div className="text-sm font-bold">Log Master Navigation</div>
                  <div className="text-xs text-muted-foreground">Open Audit Governance or browse PostgreSQL audit/log/history tables.</div>
                </div>
                <div className="max-h-[68vh] overflow-y-auto p-2 space-y-3">
                  <div>
                    <div className="px-2 pb-1 text-[11px] uppercase tracking-wide font-bold text-muted-foreground">Governance</div>
                    <button type="button" onClick={selectAuditGovernance} className={`w-full text-left px-3 py-2 rounded-xl transition-all ${isAuditGovernanceActive ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted text-foreground'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">Audit Governance</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${isAuditGovernanceActive ? 'border-primary-foreground/30 text-primary-foreground/80' : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900'}`}>governance</span>
                      </div>
                      <div className={`text-[11px] ${isAuditGovernanceActive ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>Permission, role, customer access and monthly snapshot audit views</div>
                    </button>
                  </div>
                  {grouped.map(([category, categoryTables]) => (
                    <div key={category}>
                      <div className="px-2 pb-1 text-[11px] uppercase tracking-wide font-bold text-muted-foreground">{category}</div>
                      <div className="space-y-1">
                        {categoryTables.map((table) => (
                          <button key={table.name} type="button" onClick={() => selectTable(table.name)} className={`w-full text-left px-3 py-2 rounded-xl transition-all ${activeTable?.name === table.name ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted text-foreground'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold">{table.label}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${activeTable?.name === table.name ? 'border-primary-foreground/30 text-primary-foreground/80' : categoryBadgeClass(table.category)}`}>{table.category}</span>
                            </div>
                            <div className={`text-[11px] font-mono ${activeTable?.name === table.name ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>{table.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground px-1">Governance</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={selectAuditGovernance} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${isAuditGovernanceActive ? 'bg-primary text-primary-foreground border-primary shadow' : 'bg-background text-foreground border-border hover:bg-muted'}`}>
                  Audit Governance
                </button>
              </div>
            </div>
            {grouped.map(([category, categoryTables]) => (
              <div key={category} className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground px-1">{category}</div>
                <div className="flex flex-wrap gap-2">
                  {categoryTables.map((table) => (
                    <button key={table.name} type="button" onClick={() => selectTable(table.name)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${activeTable?.name === table.name ? 'bg-primary text-primary-foreground border-primary shadow' : 'bg-background text-foreground border-border hover:bg-muted'}`} title={table.name}>
                      {table.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAuditGovernanceActive && (
        <LogMasterSectionErrorBoundary title="Audit Governance">
          <AuditGovernancePanel exportAllowed={exportAllowed} />
        </LogMasterSectionErrorBoundary>
      )}

      {!isAuditGovernanceActive && activeTable && (
        <div className="rounded-xl border border-border bg-white dark:bg-card shadow p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-bold text-foreground">{activeTable.label}</h3>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${categoryBadgeClass(activeTable.category)}`}>{activeTable.category}</span>
                {activeTable.timeColumn && <span className="text-[11px] px-2 py-0.5 rounded-full border border-border bg-muted/50 text-muted-foreground">time: {activeTable.timeColumn}</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{activeTable.description}</p>
              <p className="text-[11px] text-muted-foreground mt-1 font-mono">{activeTable.name} · {allActiveColumns.length} columns · estimate {activeTable.rowCountEstimate?.toLocaleString() ?? 'unknown'} rows</p>
            </div>
            <div className="flex items-center gap-2">
              <select className="h-9 rounded-lg border border-border bg-background px-2 text-xs" value={orderBy ?? ''} onChange={(e) => { setOrderBy(e.target.value || null); setOffset(0); }}>
                {allActiveColumns.map((c) => <option key={c.name} value={c.name}>Sort: {c.name}</option>)}
              </select>
              <select className="h-9 rounded-lg border border-border bg-background px-2 text-xs" value={order} onChange={(e) => { setOrder(e.target.value as SortOrder); setOffset(0); }}>
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-2">
            <label className="relative xl:col-span-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input className="w-full border border-border bg-background text-foreground rounded-lg pl-9 pr-3 py-2 text-sm" placeholder="Search across visible columns" value={draftFilters.search} onChange={(e) => setDraftFilters({ ...draftFilters, search: e.target.value })} />
            </label>
            <input className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" placeholder="User email" value={draftFilters.userEmail} onChange={(e) => setDraftFilters({ ...draftFilters, userEmail: e.target.value })} />
            <input className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" placeholder="Customer ID" value={draftFilters.customerId} onChange={(e) => setDraftFilters({ ...draftFilters, customerId: e.target.value })} />
            <input className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" placeholder="Incident ID" value={draftFilters.incidentId} onChange={(e) => setDraftFilters({ ...draftFilters, incidentId: e.target.value })} />
            <input className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" placeholder="Action / status" value={draftFilters.action} onChange={(e) => setDraftFilters({ ...draftFilters, action: e.target.value })} />
            <div className="flex gap-2">
              <button type="button" onClick={applyFilters} className="inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:opacity-90"><Filter className="h-3.5 w-3.5" /> Apply</button>
              <button type="button" onClick={resetFilters} className="rounded-lg border border-border bg-muted px-3 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted/80">Reset</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">From <input className="flex-1 border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" type="datetime-local" value={draftFilters.fromTs} onChange={(e) => setDraftFilters({ ...draftFilters, fromTs: e.target.value })} /></label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">To <input className="flex-1 border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm" type="datetime-local" value={draftFilters.toTs} onChange={(e) => setDraftFilters({ ...draftFilters, toTs: e.target.value })} /></label>
          </div>

          {errorMessage && <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{errorMessage}</div>}
          {loading && <div className="text-xs text-muted-foreground">Loading Log Master data…</div>}

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/60">
                <tr>
                  {activeColumns.map((col) => (
                    <th key={col.name} className="text-left px-3 py-2 whitespace-nowrap">
                      <button type="button" className="inline-flex items-center gap-1 font-bold hover:text-primary" onClick={() => toggleSort(col.name)} title={`Sort by ${col.name}`}>
                        {col.name}
                        {orderBy === col.name && <span className="text-[10px]">{order === 'asc' ? '▲' : '▼'}</span>}
                      </button>
                      {col.isSensitive && <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-300">redacted</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`${activeTable.name}-${idx}-${displayValue(row.id ?? row.ts ?? row.created_at ?? idx)}`} className="border-t border-border hover:bg-muted/40">
                    {activeColumns.map((col) => {
                      const val = displayValue(row[col.name]);
                      return <td key={col.name} className="px-3 py-2 align-top max-w-[320px] truncate" title={val}>{val}</td>;
                    })}
                  </tr>
                ))}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={Math.max(activeColumns.length, 1)} className="px-3 py-8 text-center text-muted-foreground italic">No rows match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-muted-foreground">
            <span>{total.toLocaleString()} matching rows · showing {rows.length} starting at {rowsQuery.data?.offset ?? offset}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))} disabled={offset === 0 || loading} className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-50">Prev</button>
              <button type="button" onClick={() => setOffset((current) => current + PAGE_SIZE)} disabled={offset + rows.length >= total || loading} className="px-3 py-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
